import { useState } from 'react';
import { Building, Check, Database, FileSpreadsheet, Layers, MapPin, Pill, Sparkles } from 'lucide-react';
import ScrollReveal from '../common/ScrollReveal';
import Button from '../../common/Button';

interface MasterTab {
  id: string;
  label: string;
  icon: any;
  image: string;
  subtitle: string;
  description: string;
  highlights: string[];
}

const masterTabs: MasterTab[] = [
  {
    id: 'hsn',
    label: 'HSN & GST Tax Master',
    icon: FileSpreadsheet,
    image: 'pharmacy_master_hsn.png',
    subtitle: 'Standardized Drug Taxation & HSN Mapping',
    description:
      'Pre-configure drug HSN codes (e.g. 3004, 3003) alongside multi-tier GST rates (0%, 5%, 12%, 18%). Eliminates billing tax miscalculations and enables zero-error GSTR filing.',
    highlights: [
      'Automatic tax split between CGST, SGST, and IGST for inter-state supply.',
      'One-click association with newly inwarded generic or branded formulations.',
      'Regular compliance updates aligned with national pharmaceutical tax mandates.',
    ],
  },
  {
    id: 'supplier',
    label: 'Supplier & Vendor Directory',
    icon: Building,
    image: 'pharmacy_master_supplier.png',
    subtitle: 'Verified Distributor Profiles & Drug Licenses',
    description:
      'Maintain an exhaustive database of licensed pharmaceutical distributors, super-stockists, and pharma wholesalers with complete regulatory verification.',
    highlights: [
      'Mandatory recording of Wholesale Drug License Nos. (Form 20B/21B) and GSTIN.',
      'Configurable credit payment windows (7, 15, 30, or 45 days) with payment aging.',
      'Direct purchase history lookup and historical vendor delivery reliability tracking.',
    ],
  },
  {
    id: 'location',
    label: 'Shelf & Rack Coordinates',
    icon: MapPin,
    image: 'pharmacy_master_location.png',
    subtitle: '3D Block, Column & Shelf Location Mapping',
    description:
      'Never let counter clerks search aimlessly for medications. Assign specific physical coordinates (Block, Column, Row/Shelf) to every medicine in your pharmacy store.',
    highlights: [
      'Instant shelf coordinate display on POS dispensing screens and picklists.',
      'Separate cold-chain refrigerator, high-security narcotic, and general rack zones.',
      'Drastically reduces customer waiting time at busy outpatient counters.',
    ],
  },
  {
    id: 'unit',
    label: 'Dosage Form & Unit Master',
    icon: Pill,
    image: 'pharmacy_master_unit.png',
    subtitle: 'Tablets, Syrups, Ampoules, Ointments & IV Packs',
    description:
      'Standardize administration types across all formulations. Define packaging hierarchies (Strip of 10, Box of 100, Bottle of 100ml) with precise unit conversion ratios.',
    highlights: [
      'Seamless strip-to-loose tablet conversion for prescription fulfillment.',
      'Unit consistency across POS counter sales, loose dispensing, and warehouse stock audits.',
      'Support for oral solids, parenterals, topical preparations, and inhalants.',
    ],
  },
  {
    id: 'manufacturer',
    label: 'Manufacturer Directory',
    icon: Layers,
    image: 'pharmacy_master_manufacture.png',
    subtitle: 'Pharmaceutical Brands & Parent Company Index',
    description:
      'Organize and categorize medicines by authorized pharmaceutical manufacturers (Cipla, Sun Pharma, Abbott, Dr. Reddy’s, Mankind, etc.).',
    highlights: [
      'Group branded vs generic alternatives under parent manufacturers.',
      'Track manufacturing batch standards and company recall advisories.',
      'Filter stock turnover and revenue margins by pharmaceutical company.',
    ],
  },
];

interface PharmacyMasterShowcaseProps {
  onRequestDemo?: () => void;
}

export const PharmacyMasterShowcase: React.FC<PharmacyMasterShowcaseProps> = ({
  onRequestDemo,
}) => {
  const [activeTab, setActiveTab] = useState('hsn');
  const currentTab = masterTabs.find((t) => t.id === activeTab) || masterTabs[0];

  return (
    <section id="master-catalog" className="py-16 md:py-24 overflow-hidden scroll-mt-28 bg-slate-50/60 border-y border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
          <ScrollReveal direction="down">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-[#0D7893]/10 text-[#0D7893] text-xs font-bold uppercase tracking-wider border border-[#0D7893]/20">
              <Database className="w-3.5 h-3.5" /> Centralized Master Database
            </span>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={100}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
              Standardized Pharma Catalog & Configuration
            </h2>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={200}>
            <p className="text-base sm:text-lg text-slate-600">
              A robust master catalog eliminates dispensing errors, simplifies drug discovery, and keeps your pharmacy fully compliant with tax and drug control authorities.
            </p>
          </ScrollReveal>
        </div>

        {/* Master Selector Tabs */}
        <div className="flex items-center justify-center gap-2 overflow-x-auto no-scrollbar pb-6">
          {masterTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
                  isActive
                    ? 'bg-[#0D7893] text-white shadow-md shadow-[#0D7893]/25 scale-105'
                    : 'bg-white text-slate-700 hover:bg-[#0D7893]/10 hover:text-[#0D7893] border border-slate-200'
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-[#0D7893]'}`} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content Display */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center mt-6">
          {/* Screenshot Showcase */}
          <div className="lg:col-span-7">
            <ScrollReveal direction="left" delay={150}>
              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-r from-[#0D7893]/20 to-teal-400/20 rounded-3xl blur-xl opacity-60 group-hover:opacity-90 transition duration-500" />

                <div className="relative rounded-2xl md:rounded-3xl bg-white border border-slate-200/90 shadow-2xl shadow-slate-900/10 overflow-hidden">
                  <div className="bg-slate-100/90 px-4 py-3 border-b border-slate-200/80 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-rose-400" />
                      <div className="w-3 h-3 rounded-full bg-amber-400" />
                      <div className="w-3 h-3 rounded-full bg-emerald-400" />
                      <span className="ml-2 text-xs font-medium text-slate-500 truncate">
                        Divine Pharmacy · {currentTab.label}
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0D7893] bg-[#0D7893]/10 px-2.5 py-0.5 rounded-full">
                      <Sparkles className="w-3 h-3" />
                      Master Configuration
                    </span>
                  </div>

                  <div className="relative bg-slate-50 overflow-hidden">
                    <img
                      src={`/img/svg/${currentTab.image}`}
                      alt={currentTab.label}
                      className="w-full h-auto object-cover transform transition-transform duration-500 group-hover:scale-[1.02]"
                    />
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Details Column */}
          <div className="lg:col-span-5 space-y-6">
            <ScrollReveal direction="right" delay={200}>
              <div className="space-y-5">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0D7893]/10 text-[#0D7893] text-xs font-bold uppercase tracking-wider border border-[#0D7893]/20">
                  <span>{currentTab.subtitle}</span>
                </div>

                <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                  {currentTab.label}
                </h3>

                <p className="text-base text-slate-600 leading-relaxed font-normal">
                  {currentTab.description}
                </p>

                <div className="space-y-3 pt-2">
                  {currentTab.highlights.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-3 rounded-xl bg-white border border-slate-200/80 hover:border-[#0D7893]/30 transition-all shadow-sm"
                    >
                      <div className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-[#0D7893] text-white flex items-center justify-center shadow-sm">
                        <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                      </div>
                      <p className="text-sm font-medium text-slate-700 leading-snug">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="pt-2">
                  <Button
                    size="md"
                    onClick={onRequestDemo}
                    className="gap-2 shadow-sm"
                  >
                    <span>Request Demo for Master Catalog</span>
                  </Button>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PharmacyMasterShowcase;
